import React from 'react';
import {motion} from 'framer-motion';
import {Link} from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import GoogleMaps from '../../components/GoogleMaps';
import ServicesNavigationFooter from '../../components/ServicesNavigationFooter';
import * as FiIcons from 'react-icons/fi';

const {FiTool,FiCheckCircle,FiArrowRight,FiEye,FiLayers,FiStar,FiShield}=FiIcons;

function Designboden() {
  const features=[
    {icon: FiEye,title: '3D-Optik Effekte',description: 'Beeindruckende Tiefenwirkung und realistische 3D-Designs für einzigartige Raumgestaltung.'},
    {icon: FiShield,title: 'Extrem Langlebig',description: 'Hochwertige PU-Beschichtung für maximale Haltbarkeit und Widerstandsfähigkeit.'},
    {icon: FiLayers,title: 'Fugenlos & Nahtlos',description: 'Durchgehende Oberfläche ohne Fugen für optimale Hygiene und einfache Reinigung.'}
  ];

  const applications=[
    {
      title: 'Wohnzimmer & Küche',
      description: 'Einzigartige Wohnräume mit beeindruckenden 3D-Motiven.',
      benefits: ['Wasserresistent','Pflegeleicht','Individuelle Motive','Rutschfest'],
      image: 'https://mx.ausbau.nrw/img/pu3d/pu3d_004.jpg'
    },
    {
      title: 'Badezimmer',
      description: 'Wasserfeste Designböden mit spektakulären Unterwasserwelten.',
      benefits: ['100% wasserdicht','Anti-Rutsch-Oberfläche','Schimmelresistent','Hygienisch'],
      image: 'https://mx.ausbau.nrw/img/pu3d/pu3d_002.jpg'
    },
    {
      title: 'Gewerbliche Räume',
      description: 'Repräsentative Böden für Büros,Praxen und Geschäfte.',
      benefits: ['Hochbelastbar','Professionelle Optik','Wartungsarm','Langlebig'],
      image: 'https://mx.ausbau.nrw/img/pu3d/pu3d_003.jpg'
    }
  ];

  const designOptions=[
    {name: 'Unterwasserwelten',description: 'Korallenriffe,Delfine und maritime Motive'},
    {name: 'Naturlandschaften',description: 'Wälder,Berge und Naturpanoramen'},
    {name: 'Abstrakte Kunst',description: 'Moderne Kunstwerke und geometrische Muster'},
    {name: 'Steinoptiken',description: 'Marmor,Granit und andere Natursteine'},
    {name: 'Holzoptiken',description: 'Realistische Holzstrukturen in 3D'},
    {name: 'Individuelle Motive',description: 'Ihre eigenen Bilder und Vorstellungen'}
  ];

  const advantages=[
    'Komplett individuelle Gestaltung möglich',
    'Extrem belastbar und kratzfest',
    'Einfache Reinigung und Pflege',
    'Allergikerfreundlich und hygienisch',
    'Lange Lebensdauer (15+ Jahre)',
    'Professionelle Beratung und Umsetzung'
  ];

  const process=[
    {step: '1',title: 'Beratung & Design',description: 'Motivauswahl und individuelle Gestaltungsberatung'},
    {step: '2',title: 'Untergrundvorbereitung',description: 'Professionelle Vorbereitung für optimale Haftung'},
    {step: '3',title: 'Motivdruck',description: 'Hochwertiger Digitaldruck des gewählten Motivs'},
    {step: '4',title: 'Verklebung',description: 'Fachgerechte Verlegung der Motivfolie'},
    {step: '5',title: 'Versiegelung',description: 'Mehrschichtige PU-Versiegelung für Schutz und Glanz'}
  ];

  return (
    <div className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <motion.nav
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.6}}
          className="flex items-center space-x-2 text-sm text-gray-600 mb-8"
        >
          <Link to="/" className="hover:text-mk-teal-600">Startseite</Link>
          <span>/</span>
          <Link to="/services" className="hover:text-mk-teal-600">Dienstleistungen</Link>
          <span>/</span>
          <span className="text-gray-900">PU-Designböden (3D Optik)</span>
        </motion.nav>

        {/* Header */}
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.6,delay: 0.1}}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center space-x-4 mb-6">
            <div className="w-16 h-16 bg-mk-teal-500 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiTool} className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
              PU-Designböden (3D Optik)
            </h1>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Revolutionäre Bodengestaltung mit beeindruckender 3D-Optik. Verwandeln Sie Ihre Räume in einzigartige Kunstwerke mit individuellen Motiven und hochwertiger PU-Versiegelung.
          </p>
        </motion.div>

        {/* Hero Image */}
        <motion.div
          initial={{opacity: 0,y: 30}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.8,delay: 0.2}}
          className="mb-16"
        >
          <img
            src="https://mx.ausbau.nrw/img/pu3d/pu3d_001.jpg"
            alt="PU-Designböden mit 3D-Optik"
            className="w-full h-96 object-cover rounded-2xl shadow-2xl"
          />
        </motion.div>

        {/* Features Overview */}
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.6,delay: 0.3}}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Einzigartige Eigenschaften
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature,index)=> (
              <motion.div
                key={feature.title}
                initial={{opacity: 0,y: 20}}
                animate={{opacity: 1,y: 0}}
                transition={{duration: 0.6,delay: 0.4 + index * 0.1}}
                className="text-center"
              >
                <div className="w-16 h-16 bg-mk-teal-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={feature.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Applications */}
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.6,delay: 0.5}}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Anwendungsbereiche
          </h2>
          <div className="space-y-16">
            {applications.map((app,index)=> (
              <motion.div
                key={app.title}
                initial={{opacity: 0,y: 30}}
                animate={{opacity: 1,y: 0}}
                transition={{duration: 0.6,delay: 0.6 + index * 0.1}}
                className={`flex flex-col ${index % 2===0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 lg:gap-12 items-center`}
              >
                <div className="flex-1">
                  <img
                    src={app.image}
                    alt={app.title}
                    className="w-full h-80 object-cover rounded-xl shadow-lg"
                  />
                </div>
                <div className="flex-1 space-y-6">
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                    {app.title}
                  </h3>
                  <p className="text-lg text-gray-600">
                    {app.description}
                  </p>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">
                      Vorteile:
                    </h4>
                    <ul className="grid grid-cols-2 gap-2">
                      {app.benefits.map((benefit,benefitIndex)=> (
                        <li key={benefitIndex} className="flex items-center space-x-2">
                          <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-mk-teal-500" />
                          <span className="text-gray-700 text-sm">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Design Options */}
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.6,delay: 0.7}}
          className="bg-gray-50 p-8 rounded-xl mb-20"
        >
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Designmöglichkeiten
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {designOptions.map((option,index)=> (
              <motion.div
                key={option.name}
                initial={{opacity: 0,y: 20}}
                animate={{opacity: 1,y: 0}}
                transition={{duration: 0.6,delay: 0.8 + index * 0.1}}
                className="bg-white p-6 rounded-lg shadow-md"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {option.name}
                </h3>
                <p className="text-gray-600 text-sm">
                  {option.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Advantages */}
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.6,delay: 0.8}}
          className="bg-mk-teal-50 p-8 rounded-xl mb-20"
        >
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Ihre Vorteile
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {advantages.map((advantage,index)=> (
              <motion.div
                key={advantage}
                initial={{opacity: 0,x: -20}}
                animate={{opacity: 1,x: 0}}
                transition={{duration: 0.6,delay: 0.9 + index * 0.1}}
                className="flex items-center space-x-3"
              >
                <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-mk-teal-600" />
                <span className="text-gray-700 font-medium">{advantage}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Process Section */}
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.6,delay: 0.9}}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Unser Arbeitsprozess
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            {process.map((item,index)=> (
              <motion.div
                key={item.step}
                initial={{opacity: 0,y: 20}}
                animate={{opacity: 1,y: 0}}
                transition={{duration: 0.6,delay: 1.0 + index * 0.1}}
                className="text-center"
              >
                <div className="w-16 h-16 bg-mk-red-700 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-sm">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.6,delay: 1.1}}
          className="text-center bg-gradient-to-r from-mk-teal-500 to-mk-teal-600 text-white p-12 rounded-xl"
        >
          <h2 className="text-3xl font-bold mb-4">
            Bereit für Ihren Traumboden?
          </h2>
          <p className="text-xl text-mk-teal-100 mb-8 max-w-2xl mx-auto">
            Lassen Sie uns gemeinsam Ihren individuellen 3D-Designboden planen. Von der ersten Idee bis zur perfekten Umsetzung!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center space-x-2 bg-white text-mk-teal-600 px-8 py-3 rounded-sm font-semibold hover:bg-gray-100 transition-colors"
            >
              <span>Designberatung buchen</span>
              <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Services Navigation Footer */}
      <ServicesNavigationFooter />

      {/* Google Maps Section */}
      <GoogleMaps />
    </div>
  );
}

export default Designboden;